/**
 * @iraaai/agent - Unified agent script.
 *
 * iraa.ai is no longer a chat-widget product. The runtime stays loaded on
 * customer pages but renders nothing visible until one of these activations:
 *
 *  - Live demo (primary): visitor lands with `?iraa-demo=<token>`. Token is
 *    validated; on success a split layout opens — the customer site loads in
 *    an iframe alongside an agent chat panel that runs discovery questions
 *    then drives the showcase. On invalid/expired/paused token a small
 *    branded "demo unavailable" card is shown so visitors don't hit dead air.
 *
 *  - Discovery / autonomous discovery: customer-side mapping flows.
 *    Activated by `?da_discover=`, `?da_auto_discover=`, etc.
 *
 *  - Passive learning: invisible background discovery if the product opted in.
 *
 * Customer installs (one line, applies everywhere):
 * <script src="https://cdn.iraa.ai/agent.js" data-api-key="pk_live_..." async></script>
 */

import { DiscoveryMode } from './modes/discovery.js';
import { LiveDemoIframeMode, type LiveDemoStatus } from './modes/live-demo-iframe.js';
import { IframeAgentMode } from './modes/iframe-agent.js';
import { AutonomousDiscoveryMode } from './modes/autonomous-discovery.js';
import { PassiveLearner } from './modes/passive-learner.js';
import { installSignalBus } from './capture/signal-bus.js';

const API_URL = 'https://api.iraa.ai';

// Install the customer signal bus on the global as soon as the agent script
// runs. Customer code can call `window.IraaDemo.signal({event, payload})`
// from any same-origin context — events are buffered and shipped with each
// chat turn even when the live-demo mode hasn't booted yet (e.g. the
// visitor hasn't arrived with a demo token). Idempotent install.
installSignalBus();

async function boot(): Promise<void> {
  // Read config from script tag
  const scriptEl = document.currentScript as HTMLScriptElement | null ??
    document.querySelector('script[data-api-key]');

  const apiKey = scriptEl?.getAttribute('data-api-key') ?? '';
  const apiUrl = scriptEl?.getAttribute('data-api-url') ?? API_URL;
  const productIdAttr = scriptEl?.getAttribute('data-product-id') ?? '';

  if (!apiKey) {
    console.warn('[DemoAgent] No data-api-key found on script tag');
    return;
  }

  const params = new URLSearchParams(location.search);

  // ─── Iframe-mode token resolution ───────────────────────────────────────
  // Two delivery channels, tried in order:
  //   1. window.name — set by the parent via iframe.name before iframe.src.
  //      Sync, fast, works whenever the iframe is same-origin to its parent
  //      and not navigated cross-origin afterwards. Survives SPA URL
  //      rewrites (history.replaceState) which would strip a query string.
  //   2. postMessage handshake — fallback for cases where window.name doesn't
  //      reach the inner page: sandboxed/credentialless iframes in some
  //      browser builds, cross-origin redirects that reset the browsing
  //      context name, or async script loads that miss the iframe.name set
  //      window. The child posts `iframe-boot-request` to the parent, which
  //      replies with `iframe-init` carrying the same token + kind it would
  //      have stuffed into iframe.name.
  //
  // The sync path is preferred because it adds zero latency. The fallback
  // only delays boot by a single postMessage round-trip when the sync path
  // fails — and by an explicit timeout (rather than a hang) when there's
  // no parent listener (i.e. the page wasn't really opened by an iraa
  // parent).
  const iframeTokenFromName = (key: string): string | null => {
    if (window.parent === window) return null;
    const name = typeof window.name === 'string' ? window.name : '';
    const prefix = `${key}:`;
    return name.startsWith(prefix) ? name.slice(prefix.length) : null;
  };

  // Autonomous discovery — iframe child
  {
    const fromName = iframeTokenFromName('da_auto_discover_iframe');
    if (fromName) {
      const agent = new IframeAgentMode({ sessionId: fromName, token: fromName });
      agent.start();
      console.log('[DemoAgent] IframeAgent mode - autonomous discovery (window.name)');
      return;
    }
  }

  // Full-page demo — iframe child
  {
    const fromName = iframeTokenFromName('da_demo_iframe');
    if (fromName) {
      const agent = new IframeAgentMode({ sessionId: fromName, token: fromName });
      agent.start();
      console.log('[DemoAgent] IframeAgent mode - full-page demo (window.name)');
      return;
    }
  }

  // window.name didn't carry a token. If we're inside an iframe, try the
  // postMessage handshake before falling through to top-level mode dispatch.
  if (window.parent !== window) {
    const fromHandshake = await requestIframeTokenFromParent(2000);
    if (fromHandshake) {
      const agent = new IframeAgentMode({
        sessionId: fromHandshake.token,
        token: fromHandshake.token,
      });
      agent.start();
      console.log(`[DemoAgent] IframeAgent mode - ${fromHandshake.kind} (postMessage)`);
      return;
    }
    // No parent reply within the timeout — not an iraa-driven iframe.
    // Fall through to top-level dispatch below (most likely renders nothing
    // because no `?iraa-demo=` etc. is set, but we don't want to return
    // here — a customer could still be testing the script directly).
  }

  // Autonomous discovery — top-level: renders iframe + panel on customer's domain
  const autoDiscoverToken = params.get('da_auto_discover');
  if (autoDiscoverToken) {
    await bootAutonomousDiscoveryMode(apiUrl, apiKey, autoDiscoverToken);
    return;
  }

  const discoveryToken = params.get('da_discover');

  if (discoveryToken) {
    await bootDiscoveryMode(apiUrl, apiKey, discoveryToken);
    return;
  }

  // Live demo trigger — the only way visible UI renders going forward.
  // Note: iraa-demo now boots into a split-iframe layout (LiveDemoIframeMode),
  // so the parent page never needs to resume an inline session — the iframe
  // child is the only thing that navigates within the demo.
  const demoToken = params.get('iraa-demo');

  // Catch obviously truncated tokens (e.g. shared via a client that visually
  // ellipsised the URL — `demo_q3WFe8S…` — and the ellipsis got captured into
  // the click). Real tokens are `demo_` + base64url only.
  if (demoToken && /[^A-Za-z0-9_-]/.test(demoToken)) {
    renderDemoUnavailable('truncated');
    return;
  }

  // Always probe /status: it tells us whether to start passive learning, and
  // (when a demo token is present) whether the link is valid.
  let liveDemo: (LiveDemoStatus & { available: boolean; reason?: string }) | null = null;
  try {
    const statusParams = new URLSearchParams({ key: apiKey });
    if (productIdAttr) statusParams.set('productId', productIdAttr);
    if (demoToken) statusParams.set('demoToken', demoToken);
    const statusResp = await fetch(`${apiUrl}/api/v1/session/status?${statusParams}`);
    if (statusResp.ok) {
      const data = await statusResp.json();

      if (data.passiveLearningEnabled) {
        const learner = new PassiveLearner({ apiUrl, apiKey });
        learner.start();
      }

      liveDemo = data.liveDemo ?? null;
    } else {
      console.warn(`[DemoAgent] Status check returned ${statusResp.status}`);
    }
  } catch {
    // Network failure — silent. No fallback chat icon; the customer site keeps working.
  }

  if (!demoToken) return;

  if (!liveDemo?.available) {
    renderDemoUnavailable(liveDemo?.reason ?? 'invalid');
    return;
  }

  console.log('[DemoAgent] Live demo activated (iframe split mode)');
  const liveDemoMode = new LiveDemoIframeMode({
    apiUrl,
    apiKey,
    productId: productIdAttr || undefined,
    demoToken,
    status: liveDemo,
  });
  await liveDemoMode.start();
}

/**
 * Render a small branded card explaining why a demo link did not start.
 * Reasons: 'expired' | 'consumed' | 'paused' | 'invalid' | 'no_product' | 'no_token'.
 */
function renderDemoUnavailable(reason: string): void {
  const messages: Record<string, { title: string; body: string }> = {
    expired: { title: 'This demo link has expired', body: 'Ask the person who shared it for a new link.' },
    consumed: { title: 'This demo link has already been used', body: 'Each invite link is single-use. Ask for a fresh one.' },
    paused: { title: 'Demos are temporarily paused', body: 'The team has paused live demos for now. Try again later.' },
    rate_limited: { title: 'Demos are temporarily unavailable', body: 'A lot of people are trying the demo right now. Please try again in a few minutes.' },
    truncated: { title: 'This demo link was cut off', body: 'It looks like the URL got shortened when it was shared. Ask the sender for the full link.' },
    invalid: { title: "We couldn't open this demo", body: 'The link looks invalid. Double-check the URL.' },
  };
  const m = messages[reason] ?? messages.invalid;

  const host = document.createElement('div');
  host.id = 'da-demo-unavailable';
  const shadow = host.attachShadow({ mode: 'open' });
  shadow.innerHTML = `
    <style>
      .card { position: fixed; right: 24px; bottom: 24px; max-width: 320px; padding: 16px 18px; border-radius: 12px;
        background: #111; color: #fff; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        box-shadow: 0 8px 32px rgba(0,0,0,0.18); z-index: 2147483647; }
      .t { font-weight: 600; font-size: 14px; margin-bottom: 4px; }
      .b { font-size: 13px; opacity: 0.8; line-height: 1.4; }
      .x { position: absolute; top: 8px; right: 10px; background: none; border: 0; color: inherit;
        font-size: 18px; cursor: pointer; opacity: 0.6; }
      .x:hover { opacity: 1; }
    </style>
    <div class="card" role="status">
      <button class="x" aria-label="Dismiss">×</button>
      <div class="t"></div>
      <div class="b"></div>
    </div>
  `;
  shadow.querySelector('.t')!.textContent = m.title;
  shadow.querySelector('.b')!.textContent = m.body;
  shadow.querySelector('.x')!.addEventListener('click', () => host.remove());
  document.body.appendChild(host);
}

async function bootDiscoveryMode(apiUrl: string, apiKey: string, token: string): Promise<void> {
  try {
    const resp = await fetch(`${apiUrl}/api/v1/discover/validate-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    if (!resp.ok) {
      console.log('[DemoAgent] Invalid discovery token — silent');
      return;
    }

    const data = await resp.json();
    if (!data.valid) {
      console.log('[DemoAgent] Discovery token rejected');
      return;
    }

    console.log('[DemoAgent] Discovery mode activated');
    const discovery = new DiscoveryMode({
      apiUrl,
      apiKey,
      sessionId: data.sessionId,
      token,
      voiceEnabled: data.voiceEnabled ?? false,
    });

    await discovery.start();
  } catch (err) {
    console.error('[DemoAgent] Failed to validate discovery token:', err);
  }
}

async function bootAutonomousDiscoveryMode(apiUrl: string, apiKey: string, token: string): Promise<void> {
  try {
    const resp = await fetch(`${apiUrl}/api/v1/discover/validate-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    if (!resp.ok) {
      console.log('[DemoAgent] Invalid autonomous discovery token');
      return;
    }

    const data = await resp.json();
    if (!data.valid) {
      console.log('[DemoAgent] Autonomous discovery token rejected');
      return;
    }

    // We're already on the customer's domain — use current origin as product URL
    const productUrl = data.productUrl || location.origin;

    console.log('[DemoAgent] Autonomous discovery mode activated');
    const autoDiscovery = new AutonomousDiscoveryMode({
      apiUrl,
      apiKey,
      sessionId: data.sessionId,
      token,
      productUrl,
    });

    await autoDiscovery.start();
  } catch (err) {
    console.error('[DemoAgent] Failed to boot autonomous discovery:', err);
  }
}

/**
 * Iframe-mode token postMessage handshake. Sends `iframe-boot-request` to
 * the parent and resolves with the `iframe-init` reply, or null on timeout.
 *
 * Used as a fallback when window.name didn't carry the token (sandboxed +
 * credentialless iframes can drop it in some browsers, and a cross-origin
 * redirect inside the iframe resets the browsing-context name).
 */
async function requestIframeTokenFromParent(
  timeoutMs: number,
): Promise<{ kind: 'da_demo_iframe' | 'da_auto_discover_iframe'; token: string } | null> {
  return new Promise((resolve) => {
    let settled = false;
    // Capture the window we asked, so we can verify replies came back from
    // the same browsing context (not a sibling window or a popup that
    // happens to be listening for `iframe-init` messages on the same
    // domain). `window.parent` itself can change identity if the embedding
    // page navigates between request and reply, but that case is benign —
    // a stale window won't satisfy the equality check and we just time out.
    const expectedSource = window.parent;
    const onMessage = (event: MessageEvent): void => {
      // `event.source` lets us reject replies that didn't originate from
      // the same window we asked. We deliberately don't restrict by
      // `event.origin` here — the agent ships to arbitrary customer sites
      // and we don't know the parent origin in advance.
      if (event.source !== expectedSource) return;
      const data = event.data as
        | { __iraa?: string; kind?: string; token?: string }
        | null
        | undefined;
      if (!data || data.__iraa !== 'iframe-init') return;
      if (data.kind !== 'da_demo_iframe' && data.kind !== 'da_auto_discover_iframe') return;
      if (typeof data.token !== 'string' || !data.token) return;
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve({ kind: data.kind, token: data.token });
    };
    window.addEventListener('message', onMessage);
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      resolve(null);
    }, timeoutMs);
    try {
      window.parent.postMessage({ __iraa: 'iframe-boot-request' }, '*');
    } catch {
      // Cross-origin postMessage rarely throws — even if it does we still
      // wait the timeout in case the parent posts unsolicited.
    }
  });
}

// Auto-boot when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Support DemoAgentQueue pattern for pre-init commands
const queue = (window as unknown as Record<string, unknown>).DemoAgentQueue as Array<[string, ...unknown[]]> | undefined;
if (queue && Array.isArray(queue)) {
  // Queue will be processed after boot
}
